(() => {
  if (window.hpBlockSchedule || window.Shopify?.designMode) return;
  const requests = new Map();
  const retries = new Map();
  const knownScripts = new Set();
  let timeout;

  const executable = script => !script.type || ['text/javascript', 'application/javascript', 'module'].includes(script.type);
  const scriptKey = script => `${script.type}:${script.getAttribute('src') ? new URL(script.getAttribute('src'), window.location.href).href : script.textContent.trim()}`;
  const visibleAt = (marker, now) => {
    const start = marker.dataset.start ? Number(marker.dataset.start) * 1000 : null;
    const end = marker.dataset.end ? Number(marker.dataset.end) * 1000 : null;
    return (start === null || now >= start) && (end === null || now < end);
  };

  const initializeScripts = async scripts => {
    for (const original of scripts) {
      const key = scriptKey(original);
      if (knownScripts.has(key)) continue;
      knownScripts.add(key);
      const script = document.createElement('script');
      for (const attribute of original.attributes) script.setAttribute(attribute.name, attribute.value);
      script.textContent = original.textContent;
      if (original.src) {
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = () => { knownScripts.delete(key); reject(new Error('Scheduled section script could not load')); };
          document.head.appendChild(script);
        });
      } else {
        document.head.appendChild(script);
        if (script.type !== 'module') script.remove();
      }
    }
  };

  const refresh = async section => {
    const sectionId = section.id.replace('shopify-section-', '');
    if (requests.has(sectionId)) return;
    const controller = new AbortController();
    requests.set(sectionId, controller);
    const requestTimeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('_hp_schedule', String(Date.now()));
      const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Scheduled section could not refresh');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.getElementById(section.id);
      if (!replacement || !section.isConnected) throw new Error('Scheduled section missing');
      const markers = [...replacement.querySelectorAll('template[data-hp-block-schedule]')];
      if (markers.some(marker => visibleAt(marker, Date.now()) !== (marker.dataset.rendered === 'true'))) {
        throw new Error('Scheduled section response is stale');
      }
      const scripts = [...replacement.querySelectorAll('script')].filter(executable);
      scripts.forEach(script => script.remove());
      section.querySelectorAll('video').forEach(video => video.pause());
      section.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true, detail: { sectionId } }));
      const nextSection = document.importNode(replacement, true);
      section.replaceWith(nextSection);
      await initializeScripts(scripts);
      nextSection.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId } }));
      window.hpSectionSchedule?.update();
      window.dispatchEvent(new Event('resize'));
      retries.delete(sectionId);
    } catch (error) {
      retries.set(sectionId, Date.now() + 30000);
      console.warn('[Homepage schedule] Block update will retry.', error.message);
    } finally {
      window.clearTimeout(requestTimeout);
      requests.delete(sectionId);
      update();
    }
  };

  const update = () => {
    window.clearTimeout(timeout);
    const now = Date.now();
    let nextBoundary = Infinity;
    const changedSections = new Set();
    document.querySelectorAll('template[data-hp-block-schedule]').forEach(marker => {
      const section = marker.closest('[id^="shopify-section-"]');
      if (!section) return;
      if (visibleAt(marker, now) !== (marker.dataset.rendered === 'true')) changedSections.add(section);
      [marker.dataset.start, marker.dataset.end].forEach(value => {
        const boundary = Number(value) * 1000;
        if (value && boundary > now) nextBoundary = Math.min(nextBoundary, boundary);
      });
    });
    changedSections.forEach(section => {
      const sectionId = section.id.replace('shopify-section-', '');
      const retryAt = retries.get(sectionId) || 0;
      if (retryAt > now) nextBoundary = Math.min(nextBoundary, retryAt);
      else if (!requests.has(sectionId)) void refresh(section);
    });
    if (Number.isFinite(nextBoundary)) timeout = window.setTimeout(update, Math.min(Math.max(nextBoundary - now, 1), 60000));
  };

  const start = () => {
    document.querySelectorAll('script').forEach(script => { if (executable(script)) knownScripts.add(scriptKey(script)); });
    update();
  };
  window.hpBlockSchedule = { update };
  document.addEventListener('shopify:section:load', update);
  document.addEventListener('visibilitychange', update);
  window.addEventListener('pageshow', update);
  window.addEventListener('online', () => { retries.clear(); update(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
